/* eslint-disable no-unused-vars */
import { hostOverrideOpts } from 'pages/login/hostOverrideOpts';
import {
	DEFAULT_APP_CONFIG,
	getCachedBundleAppId,
	setCachedBundleAppId,
} from 'shared/platform/frontend/utils/cachedBundleAppId.ts';
import { setManagedFavicon } from 'shared/platform/frontend/utils/setManagedFavicon.ts';
import joaFaviconUrl from 'managed-apps/business-coach/common/images/jo-icon-rounded.png';
import { CapacitorAppConfigs } from '../CapacitorAppConfigs';

const DEBUG = false;

// New: For multi-app booting
export const getBundleAppId = () => {
	if (window.bundleAppId) {
		if (DEBUG) {
			// eslint-disable-next-line no-console
			console.log(
				`[getBundleAppId] bundleAppId found on window, using bundleAppId: ${window.bundleAppId}`,
			);
		}
		return window.bundleAppId;
	}

	// Check for a bundle ID in the URL **before** loading from cache,
	// so that we can override the bundle ID for a specific domain either via URL or domain opts
	const search = new URLSearchParams(window.location.search);
	const bundleAppId = search.get('bundleAppId');
	if (bundleAppId) {
		window.bundleAppId = bundleAppId;

		// eslint-disable-next-line no-console
		console.log(
			`[getBundleAppId] bundleAppId found in URL, using bundleAppId: ${bundleAppId}, setting window.bundleAppId to ${bundleAppId}`,
		);

		// Cache this because if query string changes, we want the bundle ID to be stable
		setCachedBundleAppId(bundleAppId);
		return bundleAppId;
	}

	// Check to see if there is a specific bundle ID set by the domain we're accessing from
	const { bundleAppId: hostOverrideBundleAppId } =
		hostOverrideOpts || {};
	if (hostOverrideBundleAppId) {
		// eslint-disable-next-line no-console
		console.log(
			`[getBundleAppId] bundleAppId found in hostOverrideOpts for '${window.location.hostname}', using bundleAppId: ${hostOverrideBundleAppId}`,
		);

		window.bundleAppId = hostOverrideBundleAppId;
		return hostOverrideBundleAppId;
	}

	// No bundle ID on window, in URL, or set by domain opts? Check cache for last used.
	const cached = getCachedBundleAppId();
	if (cached) {
		// eslint-disable-next-line no-console
		console.log(
			`[getBundleAppId] bundleAppId found in localStorage, using bundleAppId: ${cached}`,
		);

		window.bundleAppId = cached;
		return cached;
	}

	// eslint-disable-next-line no-console
	console.log(
		`[getBundleAppId] No bundleAppId found in URL or in localStorage or from hostOverrideOpts, using default bundleAppId: ${DEFAULT_APP_CONFIG}`,
	);

	// Cache on window but NOT in localStorage for the default
	window.bundleAppId = DEFAULT_APP_CONFIG;
	return DEFAULT_APP_CONFIG;
};

// NEW: Support multi-app native distro, using bundleAppId encoded in the capacitor.config.ts URL
// Still working out what config props can be set
const BundleAppConfig = {
	'com.vayadriving': {
		// homeUri: '/valet',
		pushNotifications: {
			manualInit: false,
		},
		gpsPermissionRequest: {
			manualInit: false,
		},
	},

	// Test by pasting into a device's console:
	// window.location.href='https://vaya-dev-client.ngrok.io/?bundleAppId=com.meetatthemirror'
	'com.meetatthemirror': {
		// Overriding the homeUri changes what URI we go to
		// IF NO URI IS SET.
		// Note: This URI still can redirect to any other route in the <Routes> below.
		homeUri: '/a/meet-at-the-mirror',
		// Opt out of the platform-wide Facebook in-app browser block (App.js).
		// That block exists for Vaya Driving's GPS/driver flows, which genuinely
		// break inside Facebook's WebView. MATM event pages don't need GPS, so
		// when seekers open a shared event link from Facebook mobile we want it
		// to render directly instead of dead-ending on the "Open in Safari"
		// notice. Generic flag: any bundle can set it to skip the FB block.
		allowFacebookBrowser: true,
		pushNotifications: {
			// This disables PushNotifyService starting immediately on login,
			// and instead start it either after accepting terms in IntroTermsPage.js or in the MatM App.js if already onboarded
			manualInit: true,
		},
		gpsPermissionRequest: {
			// This disables GpsPermissionRequestDialog from rendering,
			// and instead we will handle the permission request in IntroTermsPage.js
			manualInit: true,
		},
	},
	// Test by pasting into a device's console:
	// window.location.href='https://vaya-dev-client.ngrok.io/?bundleAppId=com.precina.app'
	'com.precina.app': {
		// Overriding the homeUri changes what URI we go to
		// IF NO URI IS SET.
		// Note: This URI still can redirect to any other route in the <Routes> below.
		homeUri: '/a/precina-patients',
		pushNotifications: {
			// This disables PushNotifyService starting immediately on login,
			// and instead start it either after accepting terms in IntroTermsPage.js or in the MatM App.js if already onboarded
			manualInit: true,
		},
		gpsPermissionRequest: {
			// This disables GpsPermissionRequestDialog from rendering,
			// and instead we will handle the permission request in IntroTermsPage.js
			manualInit: true,
		},
	},
	// Test by pasting into a device's console:
	// window.location.href='https://vaya-dev-client.ngrok.io/?bundleAppId=com.businesscoach'
	// !DEPRECATED! Use 'com.johnoberg.app'
	// 'com.businesscoach': {
	// 	homeUri: '/a/business-coach',
	// 	favicon: joaFaviconUrl,
	// 	pushNotifications: {
	// 		// Defer push notification init -- business-coach will handle manually
	// 		manualInit: true,
	// 	},
	// 	gpsPermissionRequest: {
	// 		// Defer GPS permission dialog -- business-coach will handle in onboarding
	// 		manualInit: true,
	// 	},
	// },
	// Test by pasting into a device's console:
	// window.location.href='https://vaya-dev-client.ngrok.io/?bundleAppId=com.businesscoach'
	'com.johnoberg.app': {
		homeUri: '/a/business-coach',
		favicon: joaFaviconUrl,
		pushNotifications: {
			// Defer push notification init -- business-coach will handle manually
			manualInit: true,
		},
		gpsPermissionRequest: {
			// Defer GPS permission dialog -- business-coach will handle in onboarding
			manualInit: true,
		},
	},
	// Test by pasting into a device's console:
	// window.location.href='https://vaya-dev-client.ngrok.io/?bundleAppId=com.asc'
	'com.asc': {
		// Avatar Sharing Christ ("Room to Ask") — a faith companion, not a
		// location-aware app. Mirror the managed-app pattern (matm/precina/
		// business-coach) so the platform's GPS + push prompts never fire.
		homeUri: '/a/avatar-sharing-christ',
		pushNotifications: {
			// ASC has no use for push permission prompts on first load.
			manualInit: true,
		},
		gpsPermissionRequest: {
			// This disables GpsPermissionRequestDialog from rendering. ASC is a
			// confidential, "anonymous intimacy" faith experience — a location
			// permission prompt mid-onboarding breaks that promise outright.
			manualInit: true,
		},
	},
	// SalesForward / "SalesUpLevel" — a career-coaching / sales-training app,
	// not location-aware. Selected via hostOverrideOpts for app.salesuplevel.com
	// (see pages/login/hostOverrideOpts.js). homeUri sends the bare domain root
	// to the branded SalesForward app (which owns its own email-only login +
	// onboarding) instead of the generic Vaya platform /login.
	'com.salesforward': {
		homeUri: '/a/salesuplevel',
		pushNotifications: {
			// No push permission prompt on first load — SalesForward is a web
			// onboarding funnel, not a native push-driven app.
			manualInit: true,
		},
		gpsPermissionRequest: {
			// Not a location-aware app; never fire the platform GPS prompt.
			manualInit: true,
		},
	},
};

export const getBundleAppConfig = () => {
	const bundleAppId = getBundleAppId();
	const merged = {
		bundleAppId,
		...(CapacitorAppConfigs[bundleAppId] || {}),
		...(BundleAppConfig[bundleAppId] || {}),
	};
	// console.log(`[getBundleAppConfig] bundleAppId: ${bundleAppId}`, {
	// 	capacitorAppConfigs: CapacitorAppConfigs[bundleAppId],
	// 	bundleAppConfig: BundleAppConfig[bundleAppId],
	// 	merged,
	// 	CapacitorAppConfigs,
	// 	BundleAppConfig,
	// });
	return merged;
};

/**
 * Synchronously install the bundle's favicon. Called at module-load time
 * from App.js so the per-app icon (e.g. JO icon for com.businesscoach /
 * com.johnoberg.app) is in place before the browser paints the tab UI —
 * eliminates the flash of the default Vaya favicon on non-Vaya bundles.
 * The runtime <Head> useEffect calls the same idempotent helper later, so
 * if the favicon is already correct from this early-paint pass the second
 * call is a no-op.
 */
export const applyBundleFavicon = () => {
	const { favicon } = getBundleAppConfig();
	if (favicon) setManagedFavicon(favicon);
};
